import * as services from './services';

import { ASSISTANT_MODES, CONVERSATIONS_PAGE_SIZE } from './constants';
import { MESSAGE_ROLES, PENDING_CONVERSATION_ID, generateMessageId } from './state/reducer';
import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import aiAssistantReducer, { PANEL_MODES, initialState } from './state/reducer';
import { isEscalationOpen, normalizeAccess } from './format';

import { ActionTypes } from './state/actions';
import Poll from '/b2b/common/helpers/Poll';
import PropTypes from 'prop-types';
import { clearChatToken } from '/b2b/common/helpers/ChatAuth';
import fromState from '/b2b/common/state/selectors';
import { useSelector } from 'react-redux';

export const AIAssistantContext = createContext(null);
export const PanelModeContext = createContext(null);

/*
 * Cadence for polling an open human (Privacy Team) conversation so replies appear
 * without a manual refresh. The provider is the only consumer.
 */
const HUMAN_POLL_INTERVAL_MS = 60_000;
const HUMAN_POLL_TIMEOUT_MS = 15_000;

/*
 * Persists the active conversation across refreshes in one sessionStorage entry
 * ({ customerId, id, mode }); messages are re-fetched on restore. The customerId
 * scopes the value, so a read for a different customer is ignored and conversations
 * never leak across customers.
 */
const STORAGE_KEY = 'osano_ai_active_conversation';

const readStoredConversation = customerId => {
    try {
        const raw = window.sessionStorage?.getItem(STORAGE_KEY);
        const stored = raw ? JSON.parse(raw) : null;
        if (!stored || stored.customerId !== customerId) return null;
        return { id: stored.id, mode: stored.mode };
    } catch {
        return null;
    }
};

const writeStoredConversation = (customerId, { id, mode }) => {
    try {
        window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify({ customerId, id, mode }));
    } catch {
        // Persistence is best-effort; a failure just means no restore on refresh.
    }
};

const clearStoredConversation = () => {
    try {
        window.sessionStorage?.removeItem(STORAGE_KEY);
    } catch {
        // no-op
    }
};

const PANEL_MODE_STORAGE_KEY = 'aiAssistantPanelMode';
const OPEN_STORAGE_KEY = 'aiAssistantOpen';

const readStoredPanelMode = () => {
    try {
        const mode = window.localStorage?.getItem(PANEL_MODE_STORAGE_KEY);
        return mode === PANEL_MODES.SIDEBAR || mode === PANEL_MODES.WIDGET ? mode : null;
    } catch {
        return null;
    }
};

const writeStoredPanelMode = mode => {
    try {
        window.localStorage?.setItem(PANEL_MODE_STORAGE_KEY, mode);
    } catch {
        // no-op
    }
};

const readStoredOpen = () => {
    try {
        return window.sessionStorage?.getItem(OPEN_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

const writeStoredOpen = isOpen => {
    try {
        window.sessionStorage?.setItem(OPEN_STORAGE_KEY, String(isOpen));
    } catch {
        // no-op
    }
};

const AIAssistantProvider = ({ children }) => {
    const [state, dispatch] = useReducer(aiAssistantReducer, initialState);
    const abortControllerRef = useRef(null);
    const loadingConversationsCountRef = useRef(0);
    const pollRef = useRef(null);
    const bootstrappedRef = useRef(false);

    const customerId = useSelector(fromState.Auth.getCustomerId);

    const setPanelMode = useCallback(mode => {
        const isOverlayOpen = mode === PANEL_MODES.SIDEBAR || mode === PANEL_MODES.WIDGET;
        if (isOverlayOpen) writeStoredPanelMode(mode);
        writeStoredOpen(isOverlayOpen);
        dispatch({ type: ActionTypes.SET_PANEL_MODE, payload: mode });
    }, []);

    const openAssistant = useCallback(() => {
        setPanelMode(readStoredPanelMode() || PANEL_MODES.SIDEBAR);
    }, [setPanelMode]);

    useEffect(() => {
        if (readStoredOpen()) openAssistant();
    }, [openAssistant]);

    const setContext = useCallback(ctx => {
        dispatch({ type: ActionTypes.SET_CONTEXT, payload: ctx });
    }, []);

    /*
     * `pageSize` defaults here so every list request — including the silent refreshes
     * below — is the same size. chat-api pages by offset, so a refresh at a different
     * size would leave the next "load more" reading from the wrong place.
     */
    const loadConversations = useCallback(
        async ({ silent = false, mode, page, pageSize = CONVERSATIONS_PAGE_SIZE } = {}) => {
            if (!silent) {
                loadingConversationsCountRef.current += 1;
                dispatch({ type: ActionTypes.SET_LOADING_CONVERSATIONS, payload: true });
            }
            try {
                const response = await services.fetchConversations(mode, { page, pageSize });
                const conversations = response?.results || [];
                const hasNextPage = response?.hasNextPage || false;
                if (page && page > 1) {
                    dispatch({
                        type: ActionTypes.APPEND_CONVERSATIONS,
                        payload: { mode, conversations, page, hasNextPage },
                    });
                } else {
                    dispatch({
                        type: ActionTypes.SET_CONVERSATIONS,
                        payload: { mode, conversations, hasNextPage },
                    });
                }
                return { conversations, hasNextPage };
            } catch (error) {
                if (!silent) {
                    dispatch({
                        type: ActionTypes.SET_ERROR,
                        payload: { message: error.message, errorCode: error.errorCode },
                    });
                }
            } finally {
                if (!silent) {
                    loadingConversationsCountRef.current -= 1;
                    if (loadingConversationsCountRef.current <= 0) {
                        dispatch({ type: ActionTypes.SET_LOADING_CONVERSATIONS, payload: false });
                    }
                }
            }
        },
        []
    );

    const loadConversation = useCallback(async (conversationId, { silent = false, mode } = {}) => {
        if (!silent) {
            dispatch({ type: ActionTypes.SET_LOADING_CONVERSATION, payload: true });
            dispatch({ type: ActionTypes.CLEAR_ERROR });
        }
        try {
            const conversation = await services.fetchConversationById(conversationId, {
                mode,
            });
            if (conversation?.messages) {
                dispatch({
                    type: ActionTypes.SET_MESSAGES,
                    payload: { conversationId, messages: conversation.messages },
                });
            }
            if (conversation?.messagePagination) {
                dispatch({
                    type: ActionTypes.SET_MESSAGE_PAGINATION,
                    payload: { conversationId, ...conversation.messagePagination },
                });
            }
            // Sync conversation metadata (status, title, etc.)
            const {
                messages: _messages,
                messagePagination: _pagination,
                ...metadata
            } = conversation || {};
            if (metadata && Object.keys(metadata).length > 0) {
                dispatch({
                    type: ActionTypes.UPDATE_CONVERSATION,
                    payload: { conversationId, data: metadata },
                });
            }
            return conversation;
        } catch (error) {
            if (!silent) {
                dispatch({
                    type: ActionTypes.SET_ERROR,
                    payload: { message: error.message, errorCode: error.errorCode },
                });
            }
        } finally {
            if (!silent) {
                dispatch({ type: ActionTypes.SET_LOADING_CONVERSATION, payload: false });
            }
        }
    }, []);

    /*
     * Poll a conversation carrying an unresolved escalation so the Privacy Team's
     * replies appear without a manual refresh (there is no realtime push in v1). Lives
     * in the data layer since it's a freshness concern, not view state. Fires after the
     * first interval (selecting already fetches latest).
     */
    const isPrivacyTeamOwned = isEscalationOpen(state.activeConversation?.escalation);

    useEffect(() => {
        if (pollRef.current) {
            pollRef.current.abort();
            pollRef.current = null;
        }
        if (!state.activeConversationId || !isPrivacyTeamOwned) {
            return undefined;
        }
        const conversationId = state.activeConversationId;
        const poll = Poll(() => loadConversation(conversationId, { silent: true }), {
            delay: HUMAN_POLL_INTERVAL_MS,
            interval: HUMAN_POLL_INTERVAL_MS,
            timeout: HUMAN_POLL_TIMEOUT_MS,
        });
        pollRef.current = poll;
        return () => {
            poll.abort();
            pollRef.current = null;
        };
    }, [state.activeConversationId, isPrivacyTeamOwned, loadConversation]);

    const loadEarlierMessages = useCallback(
        async conversationId => {
            const pagination = state.messagePagination[conversationId];
            if (!pagination?.hasNext || !pagination?.nextCursor) return;

            dispatch({ type: ActionTypes.SET_LOADING_EARLIER_MESSAGES, payload: true });
            try {
                const result = await services.fetchConversationById(conversationId, {
                    cursor: pagination.nextCursor,
                    pageSize: pagination.pageSize || undefined,
                });
                dispatch({
                    type: ActionTypes.PREPEND_MESSAGES,
                    payload: { conversationId, messages: result.messages || [] },
                });
                if (result.messagePagination) {
                    dispatch({
                        type: ActionTypes.SET_MESSAGE_PAGINATION,
                        payload: { conversationId, ...result.messagePagination },
                    });
                }
            } catch (error) {
                dispatch({
                    type: ActionTypes.SET_ERROR,
                    payload: { message: error.message, errorCode: error.errorCode },
                });
            } finally {
                dispatch({ type: ActionTypes.SET_LOADING_EARLIER_MESSAGES, payload: false });
            }
        },
        [state.messagePagination]
    );

    const startConversation = useCallback(() => {
        dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: null });
    }, []);

    const setActiveConversation = useCallback(conversationId => {
        dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: conversationId });
    }, []);

    /*
     * Resolve a conversation's mode: caller-provided, else the loaded list entry, else
     * the AI default chat-api tags every conversation with. Shared by openConversation
     * and the storage restore so the two can't drift.
     */
    const resolveConversationMode = useCallback(
        (conversationId, mode) =>
            mode ??
            state.conversations.find(c => c.id === conversationId)?.mode ??
            ASSISTANT_MODES.AI,
        [state.conversations]
    );

    /*
     * Open a conversation by id: make it active and load its messages. Used instead of
     * routing so navigation works identically in every panel mode.
     */
    const openConversation = useCallback(
        (conversationId, { mode } = {}) => {
            if (!conversationId) return;
            dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: conversationId });
            loadConversation(conversationId, {
                mode: resolveConversationMode(conversationId, mode),
            });
        },
        [resolveConversationMode, loadConversation]
    );

    /*
     * Resolve which conversation to open. A deep-link id always opens it — even later
     * in the session — and counts as resolving the initial state, so the storage
     * restore is skipped. The storage restore itself runs at most once; a restored
     * conversation that 404s falls back to a blank chat.
     */
    const bootstrapActiveConversation = useCallback(
        async ({ deepLinkId } = {}) => {
            if (deepLinkId) {
                bootstrappedRef.current = true;
                openConversation(deepLinkId);
                return;
            }

            if (bootstrappedRef.current) return;
            bootstrappedRef.current = true;

            const stored = readStoredConversation(customerId);
            if (!stored?.id) return;

            dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: stored.id });
            const conversation = await loadConversation(stored.id, {
                mode: resolveConversationMode(stored.id, stored.mode),
                silent: true,
            });
            if (!conversation) {
                // Stored conversation is gone — land on a blank chat, not an error.
                dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: null });
                dispatch({ type: ActionTypes.CLEAR_ERROR });
                clearStoredConversation();
            }
        },
        [customerId, openConversation, loadConversation, resolveConversationMode]
    );

    const sendMessage = useCallback(
        async (content, options = {}) => {
            let conversationId = options.conversationId || state.activeConversationId;
            const isNewConversation = !conversationId;
            const existingConversation = state.conversations.find(c => c.id === conversationId);

            /*
             * Is a reply coming? Not while the conversation carries an unresolved
             * escalation: AI turns are paused while the Privacy Team owns it, and the
             * message is persisted and mirrored onto their ticket instead.
             *
             * This is only a prediction — the escalation status is refreshed by a poll, so
             * it can be stale in either direction. The stream reconciles both: onNoReply
             * when a reply we expected never comes, and ensureAssistantPlaceholder below
             * when one arrives on a thread we thought was paused.
             */
            const conversation =
                existingConversation ||
                (conversationId === state.activeConversationId ? state.activeConversation : null);
            const isPaused = isEscalationOpen(conversation?.escalation);
            const expectsReply = !isPaused;

            const messageKey = conversationId || PENDING_CONVERSATION_ID;

            const userMessage = {
                id: generateMessageId(),
                role: MESSAGE_ROLES.USER,
                content,
                timestamp: new Date().toISOString(),
            };

            dispatch({
                type: ActionTypes.ADD_MESSAGE,
                payload: { conversationId: messageKey, message: userMessage },
            });

            let assistantMessageId = null;
            /*
             * Opens the reply bubble and the spinner together, first caller wins. Called
             * eagerly below when a reply is expected, so the happy path still gets its
             * instant feedback, and lazily from the stream the moment a reply actually
             * starts. The stream is the source of truth: tokens arriving *are* proof an AI
             * turn ran, and APPEND_TOKEN drops tokens with no assistant bubble to land in,
             * so a stale "paused" guess would otherwise lose the whole reply.
             */
            const ensureAssistantPlaceholder = () => {
                if (assistantMessageId) return;
                assistantMessageId = generateMessageId();
                dispatch({
                    type: ActionTypes.ADD_MESSAGE,
                    payload: {
                        conversationId: conversationId || messageKey,
                        message: {
                            id: assistantMessageId,
                            role: MESSAGE_ROLES.ASSISTANT,
                            content: '',
                            access: null,
                            timestamp: new Date().toISOString(),
                        },
                    },
                });
                dispatch({ type: ActionTypes.SET_STREAMING, payload: true });
            };

            if (isNewConversation) {
                dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: messageKey });
            }

            // After SET_ACTIVE_CONVERSATION, which clears isStreaming on its way through.
            if (expectsReply) ensureAssistantPlaceholder();

            dispatch({ type: ActionTypes.CLEAR_ERROR });

            try {
                abortControllerRef.current?.abort();
                abortControllerRef.current = new AbortController();

                await services.sendChatMessageStream(content, conversationId, {
                    signal: abortControllerRef.current.signal,
                    onConversationId: returnedId => {
                        if (isNewConversation && returnedId && returnedId !== messageKey) {
                            conversationId = returnedId;
                            dispatch({
                                type: ActionTypes.ADOPT_CONVERSATION,
                                payload: { tempId: messageKey, realId: returnedId },
                            });
                        }
                    },
                    onMessageId: serverId => {
                        ensureAssistantPlaceholder();
                        if (serverId && assistantMessageId && serverId !== assistantMessageId) {
                            const activeKey = conversationId || messageKey;
                            dispatch({
                                type: ActionTypes.UPDATE_MESSAGE_ID,
                                payload: {
                                    conversationId: activeKey,
                                    oldId: assistantMessageId,
                                    newId: serverId,
                                },
                            });
                            assistantMessageId = serverId;
                        }
                    },
                    onToken: token => {
                        ensureAssistantPlaceholder();
                        const activeKey = conversationId || messageKey;
                        dispatch({
                            type: ActionTypes.APPEND_TOKEN,
                            payload: { conversationId: activeKey, token },
                        });
                    },
                    onCitations: citations => {
                        ensureAssistantPlaceholder();
                        const activeKey = conversationId || messageKey;
                        dispatch({
                            type: ActionTypes.SET_MESSAGE_CITATIONS,
                            payload: {
                                conversationId: activeKey,
                                messageId: assistantMessageId,
                                citations,
                            },
                        });
                    },
                    onAccess: rawAccess => {
                        const access = normalizeAccess(rawAccess);
                        if (!access) return;
                        ensureAssistantPlaceholder();
                        const activeKey = conversationId || messageKey;
                        dispatch({
                            type: ActionTypes.SET_MESSAGE_ACCESS,
                            payload: {
                                conversationId: activeKey,
                                messageId: assistantMessageId,
                                access,
                            },
                        });
                    },
                    onDone: () => {
                        dispatch({ type: ActionTypes.SET_STREAMING, payload: false });
                        /*
                         * A reply arrived on a conversation we thought the Privacy Team
                         * still owned, so the server has resolved the escalation and
                         * resumed AI turns. Pull the current state to un-gate the
                         * composer instead of waiting out the poll. The placeholder id
                         * is the proof a reply actually landed — a correctly paused
                         * stream ends through onNoReply and never reaches this.
                         */
                        if (isPaused && assistantMessageId && conversationId) {
                            loadConversation(conversationId, { silent: true });
                        }
                        if (isNewConversation && conversationId) {
                            // Set a provisional title from the user's message so the top bar
                            // and button appear together with the completed response.
                            // The real server-generated title will overwrite this when ready.
                            const provisionalTitle =
                                content.length > 60 ? content.substring(0, 57) + '...' : content;
                            dispatch({
                                type: ActionTypes.UPDATE_CONVERSATION,
                                payload: {
                                    conversationId,
                                    data: { title: provisionalTitle },
                                },
                            });
                        }
                    },
                    /*
                     * No AI turn ran: the conversation is with the Privacy Team, so
                     * the message was mirrored onto their ticket instead of answered.
                     * Drop the reply placeholder rather than leave an empty bubble,
                     * and re-fetch so the persisted message carries its server id.
                     */
                    onNoReply: () => {
                        dispatch({ type: ActionTypes.SET_STREAMING, payload: false });
                        if (assistantMessageId) {
                            dispatch({
                                type: ActionTypes.REMOVE_MESSAGE,
                                payload: {
                                    conversationId: conversationId || messageKey,
                                    messageId: assistantMessageId,
                                },
                            });
                            assistantMessageId = null;
                        }
                        if (conversationId) {
                            loadConversation(conversationId, { silent: true });
                        }
                    },
                    onError: error => {
                        dispatch({
                            type: ActionTypes.SET_ERROR,
                            payload: { message: error.message, errorCode: error.errorCode },
                        });
                        dispatch({ type: ActionTypes.SET_STREAMING, payload: false });
                    },
                });
            } catch (error) {
                if (error.name !== 'AbortError') {
                    dispatch({
                        type: ActionTypes.SET_ERROR,
                        payload: { message: error.message, errorCode: error.errorCode },
                    });
                    if (expectsReply) {
                        dispatch({ type: ActionTypes.SET_STREAMING, payload: false });
                    }
                }
            }

            // Refresh the list so the new/updated conversation surfaces.
            loadConversations({ silent: true, mode: ASSISTANT_MODES.AI });

            return { conversationId: conversationId || messageKey };
        },
        [
            state.activeConversationId,
            state.activeConversation,
            state.conversations,
            state.messages,
            loadConversation,
            loadConversations,
        ]
    );

    const sendFeedback = useCallback(
        async (messageId, sentiment) => {
            try {
                await services.submitFeedback(state.activeConversationId, messageId, sentiment);
            } catch (error) {
                dispatch({
                    type: ActionTypes.SET_ERROR,
                    payload: { message: error.message, errorCode: error.errorCode },
                });
            }
        },
        [state.activeConversationId]
    );

    const setError = useCallback((message, errorCode) => {
        dispatch({ type: ActionTypes.SET_ERROR, payload: { message, errorCode } });
    }, []);

    const clearError = useCallback(() => {
        dispatch({ type: ActionTypes.CLEAR_ERROR });
    }, []);

    const deleteConversation = useCallback(async conversationId => {
        await services.deleteConversation(conversationId);
        dispatch({ type: ActionTypes.REMOVE_CONVERSATION, payload: { conversationId } });
    }, []);

    const reset = useCallback(() => {
        abortControllerRef.current?.abort();
        // A chat-api token is scoped to one customer, so it must not outlive a reset.
        clearChatToken();
        dispatch({ type: ActionTypes.RESET });
    }, []);

    const escalate = useCallback(
        async (conversationId, payload) => {
            const response = await services.escalateConversation(conversationId, payload);

            /*
             * An escalation *is* a new conversation — it carries the customer's first
             * message and is paused from birth — so the widget switches to it rather than
             * marking the current one escalated. The list refresh brings it in with its
             * escalation attached, which is what puts it under the Privacy Team tab and
             * what the paused banner reads.
             */
            if (response?.conversationId) {
                await loadConversations({ silent: true, mode: ASSISTANT_MODES.AI });
                openConversation(response.conversationId);
            }
            return response;
        },
        [loadConversations, openConversation]
    );

    /*
     * On customer switch, drop the current conversation so the user lands on a blank
     * chat. The bootstrap guard is left set so persistence stays live for the new
     * customer; the switch can't mis-persist because the write effect below doesn't
     * key on customerId.
     */
    const prevCustomerIdRef = useRef(customerId);
    useEffect(() => {
        if (prevCustomerIdRef.current === customerId) return;
        prevCustomerIdRef.current = customerId;
        reset();
    }, [customerId, reset]);

    /*
     * Keep sessionStorage in sync with the active conversation so a refresh restores
     * it: write a real id, and clear when the chat goes blank (new chat, delete, or
     * customer switch). The transient pending id is ignored. Gated on bootstrap so
     * the pre-restore window doesn't run. customerId is deliberately not a dependency:
     * a switch flips it a render before reset() clears the active conversation, so
     * depending on it would persist the outgoing conversation under the incoming
     * customer.
     */
    useEffect(() => {
        if (!bootstrappedRef.current) return;
        const id = state.activeConversationId;
        if (id === PENDING_CONVERSATION_ID) return;
        if (!id) {
            clearStoredConversation();
            return;
        }
        writeStoredConversation(customerId, { id, mode: state.activeConversation?.mode });
    }, [state.activeConversationId, state.activeConversation?.mode]);

    /*
     * Memoized so consumers re-render only when state (or a callback identity)
     * changes, not on every provider render — the context is app-root and widely
     * subscribed. Callbacks are useCallback-stable, so this effectively tracks state.
     */
    const value = useMemo(
        () => ({
            ...state,
            activeMessagePagination: state.messagePagination[state.activeConversationId] || {
                nextCursor: null,
                hasNext: false,
            },
            activeMessages: state.messages[state.activeConversationId] || [],
            bootstrapActiveConversation,
            clearError,
            deleteConversation,
            escalate,
            loadConversation,
            loadConversations,
            loadEarlierMessages,
            openConversation,
            reset,
            sendFeedback,
            sendMessage,
            setActiveConversation,
            setContext,
            setError,
            openAssistant,
            setPanelMode,
            startConversation,
        }),
        [
            state,
            bootstrapActiveConversation,
            clearError,
            deleteConversation,
            escalate,
            loadConversation,
            loadConversations,
            loadEarlierMessages,
            openConversation,
            reset,
            sendFeedback,
            sendMessage,
            setActiveConversation,
            setContext,
            setError,
            openAssistant,
            setPanelMode,
            startConversation,
        ]
    );

    return (
        <PanelModeContext.Provider value={state.panelMode}>
            <AIAssistantContext.Provider value={value}>{children}</AIAssistantContext.Provider>
        </PanelModeContext.Provider>
    );
};

AIAssistantProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export { PANEL_MODES };
export default AIAssistantProvider;
